import type { View } from "@/api/types";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ISO_DATE = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?/;

/**
 * "2009-04-27" → "27 April 2009"; "2009-04" → "April 2009"; "2009" → "2009".
 * Timestamps keep only their calendar date as written (no timezone shift), because a sighting
 * date is the local date at sea. Returns null for null / unparseable input.
 */
export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = ISO_DATE.exec(iso);
  if (!m) return null;
  const [, year, month, day] = m;
  const monthName = month ? MONTHS[Number(month) - 1] : undefined;
  if (month && !monthName) return null;
  if (day && monthName) return `${Number(day)} ${monthName} ${year}`;
  if (monthName) return `${monthName} ${year}`;
  return year ?? null;
}

export function yearOf(iso: string | null | undefined): string | null {
  return iso ? (ISO_DATE.exec(iso)?.[1] ?? null) : null;
}

/** "2009 – 2024", "2016", or null when neither end is known. */
export function yearRange(first: string | null, last: string | null): string | null {
  const a = yearOf(first);
  const b = yearOf(last);
  if (a && b) return a === b ? a : `${a} – ${b}`;
  return a ?? b;
}

/** 0.973 → "97%". Values outside 0..1 are clamped: a calibrated probability can't exceed them. */
export function formatPercent(p: number): string {
  const clamped = Math.min(Math.max(p, 0), 1);
  return `${Math.round(clamped * 100)}%`;
}

/** Seconds into a video as a clock: 3.2 → "0:03", 754 → "12:34". */
export function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

export function formatCount(n: number): string {
  return new Intl.NumberFormat("en-GB").format(n);
}

export function formatCertificateNumber(n: number): string {
  return `No. ${String(n).padStart(6, "0")}`;
}

const SOURCE_LABELS: Record<string, string> = {
  lila: "LILA BC",
  sharkbook: "Sharkbook",
  inaturalist: "iNaturalist",
  gbif: "GBIF",
  firmament: "Gillspotter",
  whaleshark_org: "Wildbook for Whale Sharks",
  wikimedia: "Wikimedia Commons",
  birdshead: "Bird's Head Seascape",
  figshare: "Figshare",
};

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source.toLowerCase()] ?? source;
}

/** "cc-by-nc" → "CC BY-NC"; "cc0" → "CC0"; free text passes through. */
export function formatLicense(license: string | null): string | null {
  if (!license) return null;
  const normalized = license.trim().toLowerCase();
  if (normalized === "cc0") return "CC0";
  if (normalized.startsWith("cc-")) return `CC ${normalized.slice(3).toUpperCase()}`;
  return license;
}

const VIEW_LABELS: Record<View, string> = {
  left: "Left flank",
  right: "Right flank",
  top: "Top-down",
};

/** "left" → "Left flank"; null (unclear or unknown) → null. */
export function viewLabel(view: View | null | undefined): string | null {
  return view ? VIEW_LABELS[view] : null;
}

export function pluralize(n: number, one: string, many = `${one}s`): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}
